import { useEffect } from 'react';

const POLL_MAX_MS = 8000;
const POLL_INTERVAL_MS = 1000;
const UPGRADE_QUERY_PARAM = 'upgrade';
const UPGRADE_SUCCESS_VALUE = 'success';

interface UseUpgradeSuccessPollOptions {
	/**
	 * Caller-decided go/no-go for the whole hook. Typically composed as
	 * `hasUpgradeSuccessMarker() && isAuthenticated && isStripeUser`. When
	 * `false`, the effect does nothing — no polling, no `history.replaceState`.
	 * Gating in the caller keeps the "should this run at all?" decision
	 * readable at the call site rather than buried inside the effect body.
	 */
	enabled: boolean;
	/** Called every `POLL_INTERVAL_MS` until the saga lands or we time out. */
	refetch: () => Promise<unknown>;
	/**
	 * Optional early-stop predicate. Evaluated BEFORE the first refetch (fast
	 * path — if `true` we skip polling entirely) and after every successful
	 * `refetch()` (mid-poll — break as soon as it flips true).
	 *
	 * The predicate MUST read live state — typically `store.getState()` — not
	 * closure-captured values, which would be frozen at effect-mount time.
	 * For the resubscribe flow the caller passes `type !== basis`, which stays
	 * meaningful whether the provisioning webhook landed before or after the
	 * boot /me fetch: pre-webhook `type === 1` (poll), post-webhook `type` is
	 * any paid tier (skip / break).
	 */
	isDone?: () => boolean;
}

/**
 * SM-202: returns `true` when the current page URL carries `?upgrade=success`
 * — the marker kimuno's `resolveResubscribeSuccessUrl` appends to Stripe's
 * `return_url`. Callers compose this with auth / cohort gates to decide
 * whether the post-upgrade `fetchMe` poll should run.
 *
 * Wrap the call in `useMemo(..., [])` at the call site: Stripe redirects via
 * a hard browser navigation so the marker is either present at boot or never,
 * and stripping it later shouldn't retrigger anything upstream.
 */
export function hasUpgradeSuccessMarker(): boolean {
	if (typeof window === 'undefined') return false;
	const params = new URLSearchParams(window.location.search);
	return params.get(UPGRADE_QUERY_PARAM) === UPGRADE_SUCCESS_VALUE;
}

/**
 * SM-202: post-resubscribe-checkout redirect lands on the dashboard with
 * `?upgrade=success` appended by kimuno's `resolveResubscribeSuccessUrl`.
 * Stripe finishes its redirect before the provisioning webhook saga finishes
 * refreshing the Mongo user, so a single mount-time `fetchMe` catches stale
 * basis-tier state and the dashboard renders pre-payment. Soft-poll until the
 * saga lands (or we time out), then strip the marker so a manual reload
 * doesn't re-trigger the poll.
 *
 * Mount-once by design: the caller-supplied `refetch` is typically a fresh
 * `useAction`-wrapped function on every render, so including it in the deps
 * array would re-fire the effect endlessly. `enabled` is the only dep — when
 * it flips to true (marker present + auth hydrated + Stripe cohort) the poll
 * starts; when it flips false (e.g. logout mid-poll) cleanup cancels it.
 */
/**
 * Removes `?upgrade=success` from the current URL via `history.replaceState`
 * — preserves the page mount (no route change, no browser-history entry) and
 * keeps every other query param intact. Exported so `AppPage`'s "not
 * eligible" fallback path can share the same URL-cleanup logic instead of
 * duplicating it.
 */
export function stripUpgradeMarker(): void {
	if (typeof window === 'undefined') return;
	const url = new URL(window.location.href);
	url.searchParams.delete(UPGRADE_QUERY_PARAM);
	window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export function useUpgradeSuccessPoll({
	enabled,
	refetch,
	isDone,
}: UseUpgradeSuccessPollOptions): void {
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!enabled) return;

		// Fast path: the caller already considers the flow done (e.g. the
		// provisioning webhook landed BEFORE our boot /me fetch completed, so
		// the user tier is already paid by the time this effect runs). No poll
		// needed — just clean up the URL marker and bail.
		if (isDone?.()) {
			stripUpgradeMarker();
			return;
		}

		let cancelled = false;
		const startedAt = Date.now();

		(async () => {
			while (!cancelled && Date.now() - startedAt < POLL_MAX_MS) {
				try {
					await refetch();
				} catch {
					// Stop on the first refetch failure — the resubscribe payment
					// already succeeded server-side; the user can recover by reloading.
					break;
				}
				if (isDone?.() ?? false) break;
				await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
			}
			if (cancelled) return;
			stripUpgradeMarker();
		})();

		return () => {
			cancelled = true;
		};
		// See jsdoc above — mount-once by design.
	}, [enabled]);
}
